import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Checkbox,
  Typography,
  Divider,
  TextField,
  Grid,
  Button,
} from "@mui/material";

import {
  GradingDetailsGroupDTO,
  ParticipantGradingDTO,
  ParticipantGradingRemarkDTO,
} from "../types/ExerciseGrading";

interface Props {
  groups: GradingDetailsGroupDTO[];
  readonly: boolean;
  results: ParticipantGradingDTO[];
  remarks: ParticipantGradingRemarkDTO[];
  onChange: React.Dispatch<React.SetStateAction<ParticipantGradingDTO[]>>;
  handleSendResults: (groupId: number) => Promise<void>;
  handleSaveRemarks: (groupId: number, remarks: string) => Promise<void>;
}

const FONT_STYLE = { fontFamily: "Verdana", fontSize: "13px" };

const GradingPanel: React.FC<Props> = ({
  groups,
  readonly,
  results,
  remarks,
  onChange,
  handleSendResults,
  handleSaveRemarks,
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [initialResults, setInitialResults] = useState<ParticipantGradingDTO[]>(
    [],
  );
  const [remarksByGroup, setRemarksByGroup] = useState<Record<number, string>>(
    {},
  );
  const [initialRemarksByGroup, setInitialRemarksByGroup] = useState<
    Record<number, string>
  >({});
  const remarksInitialised = React.useRef(false);

  useEffect(() => {
    setInitialResults(JSON.parse(JSON.stringify(results)));
  }, []);

  useEffect(() => {
    if (remarksInitialised.current) return;
    remarksInitialised.current = true;
    const mapped: Record<number, string> = {};
    remarks.forEach((r) => {
      mapped[r.gradingDetailsGroupID] = r.remark ?? "";
    });
    setRemarksByGroup(mapped);
    setInitialRemarksByGroup(mapped);
  }, [remarks]);

  type ParticipantGradingUpdate = Partial<
    Omit<
      ParticipantGradingDTO,
      "gradingDetailsID" | "gradingDetailsGroupID" | "participantID"
    >
  >;

  function updateParticipantGrading(
    results: ParticipantGradingDTO[],
    gradingDetailsID: number,
    updates: ParticipantGradingUpdate,
  ): ParticipantGradingDTO[] {
    return results.map((result) =>
      result.gradingDetailsID === gradingDetailsID
        ? { ...result, ...updates }
        : result,
    );
  }

  const maxPts = useMemo(() => {
    const m: Record<number, number> = {};
    groups.forEach((g) =>
      g.gradingDetails.forEach((d) => (m[d.id] = d.points)),
    );
    return m;
  }, [groups]);

  const toggleCheck = (
    participantGradingDTO: ParticipantGradingDTO | undefined,
    initialPoints: number,
  ) => {
    if (!participantGradingDTO) return;

    const newCheckedValue = !participantGradingDTO.checked;

    const updatedResults = updateParticipantGrading(
      results,
      participantGradingDTO.gradingDetailsID,
      {
        checked: newCheckedValue,
        points: newCheckedValue ? initialPoints : 0,
      },
    );

    onChange(updatedResults);
  };

  const startEdit = (d: ParticipantGradingDTO | undefined) => {
    if (!d?.checked || readonly) return;
    setEditingId(d.gradingDetailsID);
    setEditValue(d.points.toFixed(2).toString());
  };

  const commitEdit = () => {
    if (editingId === null) return;
    const raw = parseFloat(editValue.replace(",", "."));
    const value =
      !isNaN(raw) && raw >= 0
        ? Math.min(raw, maxPts[editingId])
        : maxPts[editingId];

    const updatedResults = updateParticipantGrading(results, editingId, {
      points: value,
    });

    onChange(updatedResults);
    setEditingId(null);
  };

  const groupTotals = useMemo(
    () =>
      groups.map((group) => {
        const sum = results
          .filter((r) => r.gradingDetailsGroupID === group.id && r.checked)
          .reduce((acc, r) => acc + (r.points ?? 0), 0);

        return sum * (group.averageFactor ?? 1);
      }),
    [groups, results],
  );

  const isGroupMerged = (group: GradingDetailsGroupDTO): boolean => {
    return (
      results.find((r) => r.gradingDetailsGroupID === group.id)?.merged ?? false
    );
  };

  const hasGroupChanged = (groupId: number): boolean => {
    const currentGroupResults = results.filter(
      (r) => r.gradingDetailsGroupID === groupId,
    );
    const initialGroupResults = initialResults.filter(
      (r) => r.gradingDetailsGroupID === groupId,
    );

    if (currentGroupResults.length !== initialGroupResults.length) return true;

    const resultsChanged = currentGroupResults.some((current) => {
      const initial = initialGroupResults.find(
        (i) => i.gradingDetailsID === current.gradingDetailsID,
      );
      if (!initial) return true;
      return (
        current.checked !== initial.checked || current.points !== initial.points
      );
    });

    if (resultsChanged) return true;

    const currentRemark = remarksByGroup[groupId] ?? "";
    const initialRemark = initialRemarksByGroup[groupId] ?? "";
    return currentRemark !== initialRemark;
  };

  const handleSaveWrapper = async (groupId: number) => {
    await handleSendResults(groupId);
    await handleSaveRemarks(groupId, remarksByGroup[groupId] ?? "");

    setInitialResults((prev) =>
      prev.map((initial) => {
        const current = results.find(
          (r) =>
            r.gradingDetailsID === initial.gradingDetailsID &&
            r.gradingDetailsGroupID === groupId,
        );
        return current ? { ...current } : initial;
      }),
    );

    setInitialRemarksByGroup((prev) => ({
      ...prev,
      [groupId]: remarksByGroup[groupId] ?? "",
    }));
  };

  return (
    <Box sx={{ overflowY: "auto", pr: 2, mt: 2 }}>
      {groups
        .slice()
        .sort((a, b) => a.index - b.index)
        .map((group, gi) => (
          <Box key={group.id} mb={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, ...FONT_STYLE }}
            >
              {group.name}
            </Typography>

            <Grid container spacing={0.5} mt={0.5}>
              {group.gradingDetails
                .slice()
                .sort((a, b) => a.index - b.index)
                .map((d) => {
                  const isEditing = editingId === d.id;
                  const result = results.find(
                    (r) => r.gradingDetailsID === d.id,
                  );
                  const points = result?.checked ? result?.points : d.points;
                  const isMerged = result?.merged;
                  return (
                    <Grid container key={d.id} spacing={0.5}>
                      {!readonly && (
                        <Grid item sx={{ width: 28 }} hidden={isMerged}>
                          <Checkbox
                            size="small"
                            checked={result?.checked}
                            onChange={() => toggleCheck(result, d.points)}
                            sx={{ p: 0, ml: 1 }}
                          />
                        </Grid>
                      )}

                      <Grid
                        item
                        sx={{
                          width: 65,
                          textAlign: "right",
                          flexShrink: 0,
                          mr: 2,
                        }}
                      >
                        {isEditing ? (
                          <TextField
                            variant="standard"
                            size="small"
                            value={editValue}
                            onChange={(e) => {
                              if (!isMerged) setEditValue(e.target.value);
                            }}
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitEdit();
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            sx={{ width: 40 }}
                            inputProps={{
                              style: {
                                textAlign: "right",
                                padding: 0,
                                ...FONT_STYLE,
                              },
                            }}
                            autoFocus
                          />
                        ) : (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              ...FONT_STYLE,
                              fontWeight: 600,
                              cursor: "pointer",
                              color: points !== d.points ? "orange" : "inherit",
                            }}
                            onDoubleClick={() => {
                              if (!isMerged) startEdit(result);
                            }}
                          >
                            {points.toFixed(2)}
                            p:
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs>
                        <span style={{ fontSize: 13, fontFamily: "Verdana" }}>
                          {d.text}
                        </span>
                      </Grid>
                    </Grid>
                  );
                })}
            </Grid>

            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mt: 0.5, ...FONT_STYLE }}
            >
              Total
              {group.averageFactor !== 1
                ? ` (sum * ${group.averageFactor})`
                : ""}
              : {groupTotals[gi].toFixed(2)}p
            </Typography>

            <Box sx={{ mt: 1, mr: 3 }}>
              <TextField
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Remarks..."
                value={remarksByGroup[group.id] ?? ""}
                disabled={isGroupMerged(group)}
                onChange={(e) =>
                  setRemarksByGroup((prev) => ({
                    ...prev,
                    [group.id]: e.target.value,
                  }))
                }
                inputProps={{
                  style: {
                    ...FONT_STYLE,
                    resize: "vertical",
                  },
                }}
              />
            </Box>

            {!readonly && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 1,
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disabled={isGroupMerged(group) || !hasGroupChanged(group.id)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    boxShadow: "none",
                    px: 1.5,
                    minHeight: 30,
                    mr: 4,
                    mt: 1,
                    ...FONT_STYLE,
                  }}
                  onClick={() => {
                    handleSaveWrapper(group.id);
                  }}
                >
                  Save
                </Button>
              </Box>
            )}

            {gi !== groups.length - 1 && <Divider sx={{ mt: 1 }} />}
          </Box>
        ))}
    </Box>
  );
};

export default GradingPanel;
